import { useEffect, useState } from 'react'
import { type AuthMethods, fetchAuthMethods } from '../api/userland'

// Shown when /v1/auth/providers cannot be read (offline, a 5xx, an API that
// predates the route): every method stays offered and each button reports its
// own failure. A blip on this read must not lock everyone out of signing in.
export const EVERY_AUTH_METHOD: AuthMethods = {
  email: true,
  emailRegistration: true,
  google: true,
  telegram: true,
  tonConnect: true,
  // Not offered blind: an unread API may not send codes.
  emailCodes: false,
}

let cached: AuthMethods | null = null
let pending: Promise<AuthMethods> | null = null

/** Read on the first open of the login window, then kept for the page; a
 *  failed read is not kept, so the next open retries. Not read at page load:
 *  a visitor who never signs in never asks. */
export function loadAuthMethods(): Promise<AuthMethods> {
  if (cached) return Promise.resolve(cached)
  if (!pending) {
    pending = fetchAuthMethods().then(
      (methods) => {
        cached = methods
        return methods
      },
      (err: unknown) => {
        pending = null
        throw err
      }
    )
  }
  return pending
}

/** The methods to draw; null while the first read is in flight. */
export function useAuthMethods(): AuthMethods | null {
  const [methods, setMethods] = useState<AuthMethods | null>(cached)
  useEffect(() => {
    // Always subscribe: a read that lands between render and this effect would
    // otherwise leave the dialog on its spinner. A cached read resolves at once
    // with the same object, so React does not re-render.
    let live = true
    loadAuthMethods().then(
      (next) => {
        if (live) setMethods(next)
      },
      () => {
        if (live) setMethods(EVERY_AUTH_METHOD)
      }
    )
    return () => {
      live = false
    }
  }, [])
  return methods
}

export function resetAuthMethodsForTest(): void {
  cached = null
  pending = null
}
